import axios from 'axios'
import { adminApiClient } from '@/services/admin/baseService'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import type {
  ApiResponse,
  EvmOperationClientResourceLocator,
  EvmOperationEnvelope,
  EvmOperationList,
  EvmOperationMode,
  EvmOperationResourceType,
  EvmOperationState,
  EvmOperationType,
  EvmPendingOperationItem,
} from '@/types'

const STORAGE_PREFIX = 'cms:admin:evm-pending:v1'
const MAX_PENDING_BYTES = 512 * 1024
const MAX_REQUEST_BODY_BYTES = 64 * 1024
const API_PREFIX = '/api/v1'
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u
const OPERATION_ID_PATTERN = /^evo_[0-9a-f]{64}$/u
const OPERATION_STATES = new Set<EvmOperationState>([
  'reserved',
  'queued',
  'executing',
  'prepared',
  'broadcast',
  'unknown',
  'completed',
  'rejected',
  'failed',
])
const TERMINAL_STATES = new Set<EvmOperationState>(['completed', 'rejected', 'failed'])
const TRANSACTION_STATES = new Set<EvmOperationState>([
  'prepared',
  'broadcast',
  'unknown',
  'completed',
  'failed',
])
const PUBLIC_ERROR_MESSAGES = {
  EVM_OPERATION_REJECTED: 'Operation was rejected before signing',
  EVM_OPERATION_FAILED: 'Transaction reverted',
  EVM_BROADCAST_UNKNOWN: 'Transaction broadcast outcome is unknown',
  EVM_OPERATION_PROJECTION_CONFLICT: 'Operation projection conflicts with stored data',
} as const

type PendingOperationDraft = Pick<
  EvmPendingOperationItem,
  'operationType' | 'clientResourceLocator' | 'resourceType' | 'resourceId'
> & { requestBody: unknown }

export interface HydratedPendingOperations {
  items: EvmPendingOperationItem[]
  quarantinedKeys: string[]
}

export type EvmOperationScopeLookup =
  | { status: 'found'; operation: EvmOperationEnvelope }
  | { status: 'not_found' }
  | { status: 'unavailable' }

export interface EvmOperationScopeInspection {
  stored: EvmOperationScopeLookup
  canonical: EvmOperationScopeLookup
  hasConflict: boolean
}

export class EvmOperationStorageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EvmOperationStorageError'
  }
}

const getStorage = (): Storage => {
  if (typeof window === 'undefined' || !window.localStorage) {
    throw new EvmOperationStorageError('Persistent operation storage is unavailable')
  }
  return window.localStorage
}

const getPrefix = (adminId: string, mode: EvmOperationMode): string => {
  requireStorageIdentity(adminId)
  return `${STORAGE_PREFIX}:${mode}:${adminId}:`
}

const getStorageKey = (
  adminId: string,
  mode: EvmOperationMode,
  idempotencyKey: string
): string => {
  requireIdempotencyKey(idempotencyKey)
  return `${getPrefix(adminId, mode)}${idempotencyKey}`
}

const requireStorageIdentity = (adminId: string): void => {
  if (!/^[A-Za-z0-9_-]{1,128}$/u.test(adminId)) {
    throw new EvmOperationStorageError('Admin identity is invalid for operation storage')
  }
}

const requireIdempotencyKey = (value: string): void => {
  if (!UUID_V4_PATTERN.test(value)) {
    throw new EvmOperationStorageError('EVM operation idempotency key is invalid')
  }
}

const requireResourceId = (value: string): void => {
  if (
    value.length === 0
    || value !== value.trim()
    || value !== value.normalize('NFC')
    || [...value].length > 256
    || [...value].some(character => {
      const codePoint = character.codePointAt(0)
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)
    })
  ) {
    throw new EvmOperationStorageError('EVM operation resource ID is invalid')
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const hasExactKeys = (
  value: Record<string, unknown>,
  required: string[],
  optional: string[] = []
): boolean => {
  const allowed = new Set([...required, ...optional])
  return required.every(key => Object.prototype.hasOwnProperty.call(value, key))
    && Object.keys(value).every(key => allowed.has(key))
}

const PRE_RESERVATION_ERROR_STATUSES = new Map<string, number>([
  ['IDEMPOTENCY_KEY_REQUIRED', 400],
  ['IDEMPOTENCY_KEY_INVALID', 400],
  ['IDEMPOTENCY_KEY_REUSED', 409],
  ['EVM_OPERATION_REQUEST_INVALID', 400],
  ['EVM_OPERATION_RESOURCE_ID_REQUIRED', 400],
  ['EVM_OPERATION_RESOURCE_ID_INVALID', 400],
  ['EVM_OPERATION_RESOURCE_NOT_FOUND', 404],
  ['EVM_OPERATION_SCOPE_MISMATCH', 409],
  ['EVM_OPERATION_IDENTITY_COLLISION', 409],
  ['EVM_OPERATION_RESOURCE_FROZEN', 409],
  ['EVM_OPERATION_PAYLOAD_TOO_LARGE', 413],
])

const readExactErrorCode = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) {
    return null
  }
  const body = error.response?.data
  if (
    !isRecord(body)
    || !hasExactKeys(body, ['success', 'error'])
    || body.success !== false
    || !isRecord(body.error)
    || !hasExactKeys(body.error, ['code', 'message'])
    || typeof body.error.code !== 'string'
    || typeof body.error.message !== 'string'
  ) {
    return null
  }
  return body.error.code
}

export const isEvmOperationPreReservationError = (error: unknown): boolean => {
  const code = readExactErrorCode(error)
  return code !== null
    && axios.isAxiosError(error)
    && error.response?.status === PRE_RESERVATION_ERROR_STATUSES.get(code)
}

const isExactEvmOperationNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error)
  && error.response?.status === 404
  && readExactErrorCode(error) === 'EVM_OPERATION_NOT_FOUND'

const requireIsoTimestamp = (value: unknown): value is string =>
  typeof value === 'string'
  && new Date(value).toISOString() === value

const expectedResourceType = (operationType: EvmOperationType): EvmOperationResourceType => {
  switch (operationType) {
    case 'evm_platform_meta_refund':
      return 'refund_order'
    case 'evm_governance':
      return 'governance_deployment'
    case 'evm_fee_treasury_withdraw':
    case 'evm_fee_treasury_batch_withdraw':
      return 'fee_treasury'
  }
}

const requireLocatorString = (value: unknown): string => {
  if (typeof value !== 'string' || value.length === 0 || value !== value.trim()) {
    throw new EvmOperationStorageError('EVM operation resource locator is invalid')
  }
  return value
}

export const buildEvmOperationEndpoint = (
  operationType: EvmOperationType,
  locator: EvmOperationClientResourceLocator
): string => {
  if (!isRecord(locator)) {
    throw new EvmOperationStorageError('EVM operation resource locator is invalid')
  }
  const locatorRecord = locator as Record<string, unknown>
  switch (operationType) {
    case 'evm_platform_meta_refund': {
      if (!hasExactKeys(locatorRecord, ['orderId'])) {
        throw new EvmOperationStorageError('Refund operation locator is invalid')
      }
      const orderId = requireLocatorString(locatorRecord.orderId)
      return `/admin/orders/${encodeURIComponent(orderId)}/meta-refund`
    }
    case 'evm_governance': {
      if (!hasExactKeys(locatorRecord, ['orderId', 'action'])) {
        throw new EvmOperationStorageError('Governance operation locator is invalid')
      }
      const orderId = requireLocatorString(locatorRecord.orderId)
      const actionPaths = {
        emergency_pause: 'emergency-pause',
        emergency_resume: 'emergency-resume',
        close_contract: 'close-contract',
      } as const
      const action = locatorRecord.action
      if (typeof action !== 'string' || !(action in actionPaths)) {
        throw new EvmOperationStorageError('Governance operation action is invalid')
      }
      return `/admin/orders/${encodeURIComponent(orderId)}/${
        actionPaths[action as keyof typeof actionPaths]
      }`
    }
    case 'evm_fee_treasury_withdraw': {
      if (!hasExactKeys(locatorRecord, ['chainId', 'token'])) {
        throw new EvmOperationStorageError('Fee withdraw operation locator is invalid')
      }
      const chainId = requireLocatorString(locatorRecord.chainId)
      const token = requireLocatorString(locatorRecord.token)
      return `/admin/fee-treasury/${encodeURIComponent(chainId)}/tokens/${
        encodeURIComponent(token)
      }/withdraw`
    }
    case 'evm_fee_treasury_batch_withdraw': {
      if (!hasExactKeys(locatorRecord, ['chainId'])) {
        throw new EvmOperationStorageError('Fee batch operation locator is invalid')
      }
      const chainId = requireLocatorString(locatorRecord.chainId)
      return `/admin/fee-treasury/${encodeURIComponent(chainId)}/batch-withdraw`
    }
  }
}

const requireStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string')

const requireRequestBody = (
  value: unknown,
  operationType: EvmOperationType
): Record<string, unknown> => {
  if (!isRecord(value)) {
    throw new EvmOperationStorageError('EVM operation request body is invalid')
  }
  let serialized: string | undefined
  try {
    serialized = JSON.stringify(value)
  } catch {
    throw new EvmOperationStorageError('EVM operation request body is invalid')
  }
  if (typeof serialized !== 'string') {
    throw new EvmOperationStorageError('EVM operation request body is invalid')
  }
  if (new TextEncoder().encode(serialized).byteLength > MAX_REQUEST_BODY_BYTES) {
    throw new EvmOperationStorageError('EVM operation request body is too large')
  }
  switch (operationType) {
    case 'evm_platform_meta_refund':
      if (
        !hasExactKeys(value, ['recipients', 'amounts', 'reason'])
        || !requireStringArray(value.recipients)
        || value.recipients.length < 1
        || value.recipients.length > 50
        || !requireStringArray(value.amounts)
        || value.amounts.length !== value.recipients.length
        || typeof value.reason !== 'string'
      ) {
        throw new EvmOperationStorageError('EVM refund request body is invalid')
      }
      break
    case 'evm_governance':
      if (
        !hasExactKeys(value, [], ['notes', 'reason'])
        || Object.values(value).some(item => item !== null && typeof item !== 'string')
      ) {
        throw new EvmOperationStorageError('EVM governance request body is invalid')
      }
      break
    case 'evm_fee_treasury_withdraw':
      if (
        !hasExactKeys(value, ['recipientAddress', 'amount', 'auditReason'])
        || typeof value.recipientAddress !== 'string'
        || typeof value.amount !== 'string'
        || typeof value.auditReason !== 'string'
      ) {
        throw new EvmOperationStorageError('EVM fee request body is invalid')
      }
      break
    case 'evm_fee_treasury_batch_withdraw':
      if (
        !hasExactKeys(value, ['recipientAddress', 'withdrawals', 'auditReason'])
        || typeof value.recipientAddress !== 'string'
        || !Array.isArray(value.withdrawals)
        || value.withdrawals.length < 1
        || value.withdrawals.length > 10
        || value.withdrawals.some(withdrawal =>
          !isRecord(withdrawal)
          || !hasExactKeys(withdrawal, ['token', 'amount'])
          || typeof withdrawal.token !== 'string'
          || typeof withdrawal.amount !== 'string'
        )
        || typeof value.auditReason !== 'string'
      ) {
        throw new EvmOperationStorageError('EVM fee batch request body is invalid')
      }
      break
  }
  return value
}

const parsePendingItem = (
  value: unknown,
  expectedIdempotencyKey: string
): EvmPendingOperationItem => {
  if (!isRecord(value) || !hasExactKeys(
    value,
    [
      'idempotencyKey',
      'operationType',
      'endpoint',
      'requestBody',
      'clientResourceLocator',
      'resourceType',
      'resourceId',
      'createdAt',
    ],
    ['operationId', 'statusUrl', 'lastAttemptAt']
  )) {
    throw new EvmOperationStorageError('Stored EVM operation item has invalid keys')
  }
  if (value.idempotencyKey !== expectedIdempotencyKey) {
    throw new EvmOperationStorageError('Stored EVM operation key does not match its suffix')
  }
  requireIdempotencyKey(expectedIdempotencyKey)
  const operationType = value.operationType as EvmOperationType
  if (![
    'evm_platform_meta_refund',
    'evm_governance',
    'evm_fee_treasury_withdraw',
    'evm_fee_treasury_batch_withdraw',
  ].includes(operationType)) {
    throw new EvmOperationStorageError('Stored EVM operation type is invalid')
  }
  const resourceType = value.resourceType as EvmOperationResourceType
  if (resourceType !== expectedResourceType(operationType)) {
    throw new EvmOperationStorageError('Stored EVM operation resource type is invalid')
  }
  if (typeof value.resourceId !== 'string') {
    throw new EvmOperationStorageError('Stored EVM operation resource ID is invalid')
  }
  requireResourceId(value.resourceId)
  const endpoint = buildEvmOperationEndpoint(
    operationType,
    value.clientResourceLocator as EvmOperationClientResourceLocator
  )
  if (value.endpoint !== endpoint) {
    throw new EvmOperationStorageError('Stored EVM operation endpoint is invalid')
  }
  const requestBody = requireRequestBody(value.requestBody, operationType)
  if (!requireIsoTimestamp(value.createdAt)) {
    throw new EvmOperationStorageError('Stored EVM operation creation time is invalid')
  }
  if (value.lastAttemptAt !== undefined && !requireIsoTimestamp(value.lastAttemptAt)) {
    throw new EvmOperationStorageError('Stored EVM operation attempt time is invalid')
  }
  if (value.operationId !== undefined) {
    if (typeof value.operationId !== 'string' || !OPERATION_ID_PATTERN.test(value.operationId)) {
      throw new EvmOperationStorageError('Stored EVM operation ID is invalid')
    }
  }
  const expectedStatusUrl = value.operationId === undefined
    ? undefined
    : buildStatusUrl(resourceType, value.resourceId, value.operationId)
  const legacyStatusUrl = value.operationId === undefined
    ? undefined
    : buildStatusRequestPath(resourceType, value.resourceId, value.operationId)
  if (
    (value.statusUrl === undefined) !== (expectedStatusUrl === undefined)
    || (
      value.statusUrl !== undefined
      && value.statusUrl !== expectedStatusUrl
      && value.statusUrl !== legacyStatusUrl
    )
  ) {
    throw new EvmOperationStorageError('Stored EVM operation status URL is invalid')
  }
  return {
    idempotencyKey: expectedIdempotencyKey,
    operationType,
    endpoint,
    requestBody,
    clientResourceLocator: value.clientResourceLocator as EvmOperationClientResourceLocator,
    resourceType,
    resourceId: value.resourceId,
    ...(value.operationId === undefined ? {} : { operationId: value.operationId as string }),
    ...(expectedStatusUrl === undefined ? {} : { statusUrl: expectedStatusUrl }),
    createdAt: value.createdAt,
    ...(value.lastAttemptAt === undefined ? {} : { lastAttemptAt: value.lastAttemptAt }),
  }
}

const readPrefixEntries = (
  storage: Storage,
  prefix: string
): Array<{ key: string; value: string }> => {
  const entries: Array<{ key: string; value: string }> = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key?.startsWith(prefix)) {
      continue
    }
    const value = storage.getItem(key)
    if (value !== null) {
      entries.push({ key, value })
    }
  }
  return entries
}

const byteLength = (value: string): number => new TextEncoder().encode(value).byteLength

const writeAndReadback = (
  storage: Storage,
  key: string,
  item: EvmPendingOperationItem,
  prefix: string
): void => {
  const serialized = JSON.stringify(item)
  const otherBytes = readPrefixEntries(storage, prefix).reduce(
    (total, entry) => entry.key === key
      ? total
      : total + byteLength(entry.key) + byteLength(entry.value),
    0
  )
  if (otherBytes + byteLength(key) + byteLength(serialized) > MAX_PENDING_BYTES) {
    throw new EvmOperationStorageError('Persistent EVM operation storage is full')
  }
  try {
    storage.setItem(key, serialized)
    if (storage.getItem(key) !== serialized) {
      throw new Error('readback mismatch')
    }
  } catch {
    throw new EvmOperationStorageError('Unable to persist the EVM operation key')
  }
}

const createUuidV4 = (): string => {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    throw new EvmOperationStorageError('Secure operation key generation is unavailable')
  }
  const value = crypto.randomUUID().toLowerCase()
  requireIdempotencyKey(value)
  return value
}

export const createPendingEvmOperation = (
  adminId: string,
  mode: EvmOperationMode,
  draft: PendingOperationDraft
): EvmPendingOperationItem => {
  const storage = getStorage()
  const idempotencyKey = createUuidV4()
  const endpoint = buildEvmOperationEndpoint(draft.operationType, draft.clientResourceLocator)
  if (draft.resourceType !== expectedResourceType(draft.operationType)) {
    throw new EvmOperationStorageError('EVM operation resource type does not match its action')
  }
  requireResourceId(draft.resourceId)
  const item: EvmPendingOperationItem = {
    idempotencyKey,
    operationType: draft.operationType,
    endpoint,
    requestBody: requireRequestBody(draft.requestBody, draft.operationType),
    clientResourceLocator: draft.clientResourceLocator,
    resourceType: draft.resourceType,
    resourceId: draft.resourceId,
    createdAt: new Date().toISOString(),
  }
  const prefix = getPrefix(adminId, mode)
  writeAndReadback(storage, getStorageKey(adminId, mode, idempotencyKey), item, prefix)
  return item
}

export const hydratePendingEvmOperations = (
  adminId: string,
  mode: EvmOperationMode
): HydratedPendingOperations => {
  const storage = getStorage()
  const prefix = getPrefix(adminId, mode)
  const entries = readPrefixEntries(storage, prefix)
  const totalBytes = entries.reduce(
    (total, entry) => total + byteLength(entry.key) + byteLength(entry.value),
    0
  )
  if (totalBytes > MAX_PENDING_BYTES) {
    throw new EvmOperationStorageError('Persistent EVM operation storage is over capacity')
  }
  const items: EvmPendingOperationItem[] = []
  const quarantinedKeys: string[] = []
  entries.forEach(entry => {
    const suffix = entry.key.slice(prefix.length)
    try {
      items.push(parsePendingItem(JSON.parse(entry.value), suffix))
    } catch {
      quarantinedKeys.push(entry.key)
    }
  })
  return {
    items: items.sort((left, right) => left.createdAt.localeCompare(right.createdAt)),
    quarantinedKeys,
  }
}

const buildStatusRequestPath = (
  resourceType: EvmOperationResourceType,
  resourceId: string,
  operationId: string
): string => `/admin/evm-operations/${resourceType}/${
  encodeURIComponent(resourceId)
}/${operationId}`

const buildStatusUrl = (
  resourceType: EvmOperationResourceType,
  resourceId: string,
  operationId: string
): string => `${API_PREFIX}${buildStatusRequestPath(resourceType, resourceId, operationId)}`

const statusRequestPath = (statusUrl: string): string => {
  if (!statusUrl.startsWith(`${API_PREFIX}/admin/evm-operations/`)) {
    throw new EvmOperationStorageError('Stored EVM operation status URL is invalid')
  }
  return statusUrl.slice(API_PREFIX.length)
}

const CANONICAL_ADDRESS_PATTERN = /^0x[0-9a-f]{40}$/u
const CANONICAL_AMOUNT_PATTERN = /^[1-9][0-9]*$/u
const ZERO_EVM_ADDRESS = `0x${'0'.repeat(40)}`

const requireTerminalResult = (
  value: unknown,
  operationType: EvmOperationType,
  state: 'completed' | 'failed'
): void => {
  if (!isRecord(value)) {
    throw new Error('EVM operation result is invalid')
  }
  switch (operationType) {
    case 'evm_platform_meta_refund':
      if (
        !hasExactKeys(value, ['refundId', 'refundStatusId'])
        || typeof value.refundId !== 'string'
        || !/^rf_[0-9a-f]{64}$/u.test(value.refundId)
        || value.refundStatusId !== (state === 'completed' ? 'ros_pending' : 'ros_failed')
      ) {
        throw new Error('EVM refund operation result is invalid')
      }
      return
    case 'evm_governance':
      if (
        !hasExactKeys(value, ['governanceEventId', 'action'])
        || typeof value.governanceEventId !== 'string'
        || !/^cge_[0-9a-f]{64}$/u.test(value.governanceEventId)
        || !['emergency_pause', 'emergency_resume', 'close_contract'].includes(
          value.action as string
        )
      ) {
        throw new Error('EVM governance operation result is invalid')
      }
      return
    case 'evm_fee_treasury_withdraw':
      if (
        !hasExactKeys(
          value,
          [
            'chainId',
            'compatibility',
            'token',
            'recipientAddress',
            'amount',
            'signerRole',
            'signerAddress',
          ],
          ['auditDegraded']
        )
        || typeof value.chainId !== 'string'
        || !CANONICAL_AMOUNT_PATTERN.test(value.chainId)
        || value.compatibility !== 'evm'
        || typeof value.token !== 'string'
        || !CANONICAL_ADDRESS_PATTERN.test(value.token)
        || value.token === ZERO_EVM_ADDRESS
        || typeof value.recipientAddress !== 'string'
        || !CANONICAL_ADDRESS_PATTERN.test(value.recipientAddress)
        || typeof value.amount !== 'string'
        || !CANONICAL_AMOUNT_PATTERN.test(value.amount)
        || value.signerRole !== 'deployment'
        || typeof value.signerAddress !== 'string'
        || !CANONICAL_ADDRESS_PATTERN.test(value.signerAddress)
        || (value.auditDegraded !== undefined && value.auditDegraded !== true)
      ) {
        throw new Error('EVM fee operation result is invalid')
      }
      return
    case 'evm_fee_treasury_batch_withdraw': {
      if (
        !hasExactKeys(
          value,
          [
            'chainId',
            'compatibility',
            'recipientAddress',
            'signerRole',
            'signerAddress',
            'withdrawals',
          ],
          ['auditDegraded']
        )
        || typeof value.chainId !== 'string'
        || !CANONICAL_AMOUNT_PATTERN.test(value.chainId)
        || value.compatibility !== 'evm'
        || typeof value.recipientAddress !== 'string'
        || !CANONICAL_ADDRESS_PATTERN.test(value.recipientAddress)
        || value.signerRole !== 'deployment'
        || typeof value.signerAddress !== 'string'
        || !CANONICAL_ADDRESS_PATTERN.test(value.signerAddress)
        || !Array.isArray(value.withdrawals)
        || value.withdrawals.length < 1
        || value.withdrawals.length > 10
        || (value.auditDegraded !== undefined && value.auditDegraded !== true)
      ) {
        throw new Error('EVM fee batch operation result is invalid')
      }
      const tokens = new Set<string>()
      value.withdrawals.forEach(withdrawal => {
        if (
          !isRecord(withdrawal)
          || !hasExactKeys(withdrawal, ['token', 'amount'])
          || typeof withdrawal.token !== 'string'
          || !CANONICAL_ADDRESS_PATTERN.test(withdrawal.token)
          || withdrawal.token === ZERO_EVM_ADDRESS
          || typeof withdrawal.amount !== 'string'
          || !CANONICAL_AMOUNT_PATTERN.test(withdrawal.amount)
          || tokens.has(withdrawal.token)
        ) {
          throw new Error('EVM fee batch withdrawal result is invalid')
        }
        tokens.add(withdrawal.token)
      })
    }
  }
}

const parseEnvelope = (
  value: unknown,
  expected: Pick<EvmPendingOperationItem, 'operationType' | 'resourceType' | 'resourceId'>,
  mode: EvmOperationMode
): EvmOperationEnvelope => {
  if (!isRecord(value) || !hasExactKeys(
    value,
    [
      'schemaVersion',
      'operationId',
      'operationType',
      'mode',
      'resourceType',
      'resourceId',
      'sequence',
      'state',
      'statusUrl',
      'createdAt',
      'updatedAt',
    ],
    ['txHash', 'result', 'error', 'terminalAt']
  ) || value.schemaVersion !== 'v1') {
    throw new Error('EVM operation response is invalid')
  }
  const operationId = value.operationId
  const state = value.state as EvmOperationState
  if (
    typeof operationId !== 'string'
    || !OPERATION_ID_PATTERN.test(operationId)
    || value.operationType !== expected.operationType
    || value.mode !== mode
    || value.resourceType !== expected.resourceType
    || value.resourceId !== expected.resourceId
    || !OPERATION_STATES.has(state)
    || !Number.isSafeInteger(value.sequence)
    || (value.sequence as number) < 1
  ) {
    throw new Error('EVM operation response scope is invalid')
  }
  const statusUrl = buildStatusUrl(expected.resourceType, expected.resourceId, operationId)
  if (value.statusUrl !== statusUrl) {
    throw new Error('EVM operation response status URL is invalid')
  }
  if (!requireIsoTimestamp(value.createdAt) || !requireIsoTimestamp(value.updatedAt)) {
    throw new Error('EVM operation response timestamp is invalid')
  }
  const isTerminal = TERMINAL_STATES.has(state)
  if (
    (value.terminalAt !== undefined) !== isTerminal
    || (value.terminalAt !== undefined && !requireIsoTimestamp(value.terminalAt))
  ) {
    throw new Error('EVM operation terminal timestamp is invalid')
  }
  const shouldHaveTransaction = TRANSACTION_STATES.has(state)
  if (
    (value.txHash !== undefined) !== shouldHaveTransaction
    || (value.txHash !== undefined
      && (typeof value.txHash !== 'string' || !/^0x[0-9a-f]{64}$/u.test(value.txHash)))
  ) {
    throw new Error('EVM operation transaction hash is invalid')
  }
  const shouldHaveResult = state === 'completed' || state === 'failed'
  if ((value.result !== undefined) !== shouldHaveResult) {
    throw new Error('EVM operation result is invalid')
  }
  if (shouldHaveResult) {
    requireTerminalResult(value.result, expected.operationType, state)
  }
  requirePublicError(value.error, state)
  return value as unknown as EvmOperationEnvelope
}

const requirePublicError = (value: unknown, state: EvmOperationState): void => {
  if (value === undefined) {
    if (state === 'rejected' || state === 'failed' || state === 'unknown') {
      throw new Error('EVM operation public error is missing')
    }
    return
  }
  if (!isRecord(value) || !hasExactKeys(value, ['code', 'message'])) {
    throw new Error('EVM operation public error is invalid')
  }
  const code = value.code as keyof typeof PUBLIC_ERROR_MESSAGES
  if (!(code in PUBLIC_ERROR_MESSAGES) || value.message !== PUBLIC_ERROR_MESSAGES[code]) {
    throw new Error('EVM operation public error is invalid')
  }
  const validForState = code === 'EVM_OPERATION_REJECTED'
    ? state === 'rejected'
    : code === 'EVM_OPERATION_FAILED'
      ? state === 'failed'
      : code === 'EVM_BROADCAST_UNKNOWN'
        ? state === 'unknown'
        : !TERMINAL_STATES.has(state)
  if (!validForState) {
    throw new Error('EVM operation public error does not match its state')
  }
}

const storeEnvelope = (
  adminId: string,
  mode: EvmOperationMode,
  item: EvmPendingOperationItem,
  envelope: EvmOperationEnvelope
): EvmPendingOperationItem => {
  const storage = getStorage()
  const key = getStorageKey(adminId, mode, item.idempotencyKey)
  if (TERMINAL_STATES.has(envelope.state)) {
    storage.removeItem(key)
    return item
  }
  const updated: EvmPendingOperationItem = {
    ...item,
    operationId: envelope.operationId,
    statusUrl: envelope.statusUrl,
  }
  writeAndReadback(storage, key, updated, getPrefix(adminId, mode))
  return updated
}

const responseEnvelope = (
  value: unknown,
  item: EvmPendingOperationItem,
  mode: EvmOperationMode
): EvmOperationEnvelope => {
  if (
    !isRecord(value)
    || !hasExactKeys(value, ['success', 'data'])
    || value.success !== true
    || !isRecord(value.data)
  ) {
    throw new Error('EVM operation response is missing its operation')
  }
  return parseEnvelope(value.data, item, mode)
}

const terminalErrorEnvelope = (
  error: unknown,
  item: EvmPendingOperationItem,
  mode: EvmOperationMode
): EvmOperationEnvelope | null => {
  if (!axios.isAxiosError(error)) {
    return null
  }
  const status = error.response?.status
  if (status !== 409 && status !== 422) {
    return null
  }
  try {
    const body = error.response?.data
    if (
      !isRecord(body)
      || !hasExactKeys(body, ['success', 'error', 'data'])
      || body.success !== false
      || !isRecord(body.error)
      || !hasExactKeys(body.error, ['code', 'message'])
      || !isRecord(body.data)
    ) {
      return null
    }
    const envelope = parseEnvelope(body.data, item, mode)
    const expectedState = status === 409 ? 'rejected' : 'failed'
    if (
      envelope.state !== expectedState
      || !envelope.error
      || body.error.code !== envelope.error.code
      || body.error.message !== envelope.error.message
    ) {
      return null
    }
    return envelope
  } catch {
    return null
  }
}

export const submitPendingEvmOperation = async (
  adminId: string,
  mode: EvmOperationMode,
  input: EvmPendingOperationItem
): Promise<EvmOperationEnvelope> => {
  const storage = getStorage()
  const item = parsePendingItem(input, input.idempotencyKey)
  const attempted: EvmPendingOperationItem = {
    ...item,
    lastAttemptAt: new Date().toISOString(),
  }
  writeAndReadback(
    storage,
    getStorageKey(adminId, mode, item.idempotencyKey),
    attempted,
    getPrefix(adminId, mode)
  )
  const modeConfig = buildModeAwareConfig(mode)
  try {
    const response = await adminApiClient.post<ApiResponse<EvmOperationEnvelope>>(
      attempted.endpoint,
      attempted.requestBody,
      {
        ...modeConfig,
        headers: {
          ...(modeConfig?.headers ?? {}),
          'Idempotency-Key': attempted.idempotencyKey,
          'X-EVM-Resource-Id': attempted.resourceId,
        },
      }
    )
    const envelope = responseEnvelope(response.data, attempted, mode)
    const expectedStatus = envelope.state === 'completed' ? 200 : 202
    if (response.status !== expectedStatus) {
      throw new Error('EVM operation HTTP status does not match its state')
    }
    storeEnvelope(adminId, mode, attempted, envelope)
    return envelope
  } catch (error) {
    const envelope = terminalErrorEnvelope(error, attempted, mode)
    if (envelope) {
      storeEnvelope(adminId, mode, attempted, envelope)
      return envelope
    }
    throw error
  }
}

export const getEvmOperationStatus = async (
  item: EvmPendingOperationItem,
  mode: EvmOperationMode
): Promise<EvmOperationEnvelope> => {
  const parsed = parsePendingItem(item, item.idempotencyKey)
  if (!parsed.statusUrl || !parsed.operationId) {
    throw new Error('EVM operation status is not available before reservation')
  }
  const response = await adminApiClient.get<ApiResponse<EvmOperationEnvelope>>(
    statusRequestPath(parsed.statusUrl),
    buildModeAwareConfig(mode)
  )
  return responseEnvelope(response.data, parsed, mode)
}

export const lookupEvmOperation = async (
  item: EvmPendingOperationItem,
  mode: EvmOperationMode
): Promise<EvmOperationEnvelope> => {
  const parsed = parsePendingItem(item, item.idempotencyKey)
  return lookupEvmOperationAtResource(parsed, parsed.resourceId, mode)
}

const lookupEvmOperationAtResource = async (
  item: EvmPendingOperationItem,
  resourceId: string,
  mode: EvmOperationMode
): Promise<EvmOperationEnvelope> => {
  requireResourceId(resourceId)
  const modeConfig = buildModeAwareConfig(mode)
  const response = await adminApiClient.get<ApiResponse<EvmOperationEnvelope>>(
    `/admin/evm-operations/${item.resourceType}/${
      encodeURIComponent(resourceId)
    }/by-idempotency-key`,
    {
      ...modeConfig,
      headers: {
        ...(modeConfig?.headers ?? {}),
        'Idempotency-Key': item.idempotencyKey,
      },
    }
  )
  return responseEnvelope(response.data, { ...item, resourceId }, mode)
}

const inspectScope = async (
  item: EvmPendingOperationItem,
  resourceId: string,
  mode: EvmOperationMode
): Promise<EvmOperationScopeLookup> => {
  try {
    return {
      status: 'found',
      operation: await lookupEvmOperationAtResource(item, resourceId, mode),
    }
  } catch (error) {
    return isExactEvmOperationNotFound(error)
      ? { status: 'not_found' }
      : { status: 'unavailable' }
  }
}

export const inspectPendingEvmOperationScopes = async (
  input: EvmPendingOperationItem,
  canonicalResourceId: string,
  mode: EvmOperationMode
): Promise<EvmOperationScopeInspection> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  requireResourceId(canonicalResourceId)
  if (canonicalResourceId === item.resourceId) {
    throw new EvmOperationStorageError('Stored and canonical EVM operation scopes are identical')
  }
  const [stored, canonical] = await Promise.all([
    inspectScope(item, item.resourceId, mode),
    inspectScope(item, canonicalResourceId, mode),
  ])
  return {
    stored,
    canonical,
    hasConflict: stored.status === 'found' && canonical.status === 'found',
  }
}

export const reconcilePendingEvmOperation = async (
  adminId: string,
  mode: EvmOperationMode,
  input: EvmPendingOperationItem
): Promise<EvmOperationEnvelope> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  let envelope: EvmOperationEnvelope
  if (item.statusUrl) {
    envelope = await getEvmOperationStatus(item, mode)
  } else {
    try {
      envelope = await lookupEvmOperation(item, mode)
    } catch (error) {
      if (isExactEvmOperationNotFound(error)) {
        if (!item.lastAttemptAt) {
          throw new Error('Unsent EVM operation draft has no server operation')
        }
        return submitPendingEvmOperation(adminId, mode, item)
      }
      throw error
    }
  }
  storeEnvelope(adminId, mode, item, envelope)
  return envelope
}

export const listEvmOperations = async (
  resourceType: EvmOperationResourceType,
  resourceId: string,
  mode: EvmOperationMode,
  afterSequence = 0,
  limit = 50
): Promise<EvmOperationList> => {
  requireResourceId(resourceId)
  if (!Number.isSafeInteger(afterSequence) || afterSequence < 0) {
    throw new Error('EVM operation sequence is invalid')
  }
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
    throw new Error('EVM operation list limit is invalid')
  }
  const response = await adminApiClient.get<ApiResponse<EvmOperationList>>(
    `/admin/evm-operations/${resourceType}/${encodeURIComponent(resourceId)}`,
    {
      ...buildModeAwareConfig(mode),
      params: { afterSequence, limit },
    }
  )
  const body = response.data
  const data = body?.data
  if (
    !isRecord(body)
    || !hasExactKeys(body, ['success', 'data'])
    || body.success !== true
    || !isRecord(data)
    || !hasExactKeys(data, [
      'schemaVersion',
      'mode',
      'resourceType',
      'resourceId',
      'items',
      'nextAfterSequence',
    ])
    || data.schemaVersion !== 'v1'
    || data.mode !== mode
    || data.resourceType !== resourceType
    || data.resourceId !== resourceId
    || !Array.isArray(data.items)
  ) {
    throw new Error('EVM operation list response is invalid')
  }
  const items = data.items.map(item => {
    if (!isRecord(item) || typeof item.operationType !== 'string') {
      throw new Error('EVM operation list item is invalid')
    }
    const operationType = item.operationType as EvmOperationType
    if (expectedResourceType(operationType) !== resourceType) {
      throw new Error('EVM operation list item scope is invalid')
    }
    const parsed = parseEnvelope(item, { operationType, resourceType, resourceId }, mode)
    if (TERMINAL_STATES.has(parsed.state)) {
      throw new Error('EVM operation list returned a terminal item')
    }
    return parsed
  })
  for (let index = 0; index < items.length; index += 1) {
    const previousSequence = index === 0 ? afterSequence : items[index - 1].sequence
    if (items[index].sequence <= previousSequence) {
      throw new Error('EVM operation list sequence is invalid')
    }
  }
  const expectedNextSequence = items.at(-1)?.sequence ?? null
  if (data.nextAfterSequence !== expectedNextSequence) {
    throw new Error('EVM operation list cursor is invalid')
  }
  return {
    schemaVersion: 'v1',
    mode,
    resourceType,
    resourceId,
    items,
    nextAfterSequence: expectedNextSequence,
  }
}

export const evmOperationService = {
  createPending: createPendingEvmOperation,
  hydratePending: hydratePendingEvmOperations,
  submitPending: submitPendingEvmOperation,
  getStatus: getEvmOperationStatus,
  lookup: lookupEvmOperation,
  inspectScopes: inspectPendingEvmOperationScopes,
  isPreReservationError: isEvmOperationPreReservationError,
  reconcilePending: reconcilePendingEvmOperation,
  list: listEvmOperations,
}
